import {
  WSMenuItem,
  WSTable,
  WSTableColumn
} from "@wingspanhq/fe-component-library";
import React, { useMemo } from "react";
import { vaultFilesApi } from "@api";
import { queriesVaultFiles } from "@query";
import { downloadFileFromBuffer } from "../../../../utils/files";
import { WorkLogAttachmentNameCell } from "./WorkLogAttachmentNameCell";

type AttachmentData = {
  fileId: string;
};

type Props = {
  fileIds: string[];
  onRemove?: (fileId: string) => void;
};

/**
 * The work log's attachments, read from the v3 file vault.
 *
 * `WorkLog.attachmentIds` spans two id spaces: files attached since the v3
 * migration are VaultFiles and read back fine, while anything attached before
 * it is a legacy MemberFile whose only owner — the v1 files service — is a hard
 * kill switch in this app. Such a row reports as unavailable rather than
 * pretending to be downloadable; see `useBillingAttachmentFile`, which resolves
 * the same two id spaces for invoice/payable attachments.
 */
export const WorkLogAttachmentsTable: React.FC<Props> = ({
  fileIds,
  onRemove
}) => {
  const tableData = useMemo(
    () => fileIds.map(fileId => ({ id: fileId, data: { fileId } })),
    [fileIds]
  );

  const columns: WSTableColumn<AttachmentData>[] = useMemo(
    () => [
      {
        renderFunction: item => (
          <WorkLogAttachmentNameCell fileId={item.data.fileId} />
        )
      }
    ],
    []
  );

  // The content read is typed `void` by the SDK (the endpoint answers a
  // redirect), so the bytes come off the raw response rather than a typed body.
  const download = async (fileId: string) => {
    const vaultFile = await queriesVaultFiles.getVaultFile.fetchQueryData({
      fileId
    });
    const response = await vaultFilesApi.getVaultFileContentRaw({ fileId });

    downloadFileFromBuffer(
      await response.raw.arrayBuffer(),
      vaultFile.filename ?? vaultFile.title ?? "download",
      vaultFile.mimeType
    );
  };

  const rowMenuActions = useMemo(
    () =>
      ({ data }: { data: AttachmentData }): WSMenuItem[] => {
        const items: WSMenuItem[] = [
          {
            label: "Download",
            icon: "download",
            onClick: () => download(data.fileId)
          }
        ];
        if (onRemove) {
          items.push(
            { separator: true },
            {
              label: "Remove",
              icon: "trash",
              onClick: () => onRemove(data.fileId),
              iconProps: { color: "red400" },
              labelProps: { color: "red400" }
            }
          );
        }
        return items;
      },
    [onRemove]
  );

  return (
    <WSTable<AttachmentData>
      showHeader={false}
      tableData={tableData}
      columns={columns}
      rowActions={({ data }) => {
        return [
          {
            text: "",
            onlyIcon: true,
            icon: "download",
            onClick: () => download(data.fileId)
          }
        ];
      }}
      rowMenuActions={rowMenuActions}
    />
  );
};
